import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, PlayCircle, Bot, Sparkles, FileCheck2, X } from "lucide-react";
import { EASE } from "./Reveal";

export default function Hero() {
  const ref = useRef(null);
  const TITLE_LINES = ["Comprenez votre parcours.", "Préparez votre avenir en France."];
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yMockup = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yBubble = useTransform(scrollYProgress, [0, 1], [0, 150]);
  const [showVideo, setShowVideo] = useState(false);

  return (
    <section id="accueil" ref={ref} className="relative overflow-hidden pb-16 pt-[130px] sm:pb-24 lg:pt-[150px]">
      <div className="pointer-events-none absolute -right-40 -top-40 h-[560px] w-[560px] rounded-full bg-bleu-light blur-3xl" />
      <div className="pointer-events-none absolute -left-52 top-72 h-[420px] w-[420px] rounded-full bg-[#FFF1F2] blur-3xl" />

      <div className="container-integra relative grid items-center gap-14 lg:grid-cols-2">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="overline-tag"
            data-testid="hero-overline"
          >
            <span aria-hidden>🇫🇷</span> Votre parcours d'intégration en France
          </motion.div>

          <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-slate-900 sm:text-5xl lg:text-[3.4rem]">
            {TITLE_LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 1 ? "text-bleu" : ""}`}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.95, delay: 0.15 + i * 0.14, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55, ease: EASE }}
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
          >
            INTEGRA FRANCE vous accompagne à chaque étape : information, formation civique, préparation à l'examen,
            démarches administratives et découverte de la vie en France.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.68, ease: EASE }}
            className="mt-5 inline-flex items-start gap-3 rounded-2xl border border-bleu/15 bg-bleu-light/60 px-5 py-4"
          >
            <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-bleu" />
            <p className="text-sm font-semibold leading-relaxed text-bleu">
              Un accompagnement simple, personnalisé et accessible avec l'intelligence artificielle.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8, ease: EASE }}
            className="mt-8 flex flex-col gap-3 sm:flex-row"
          >
            <Link to="/signup" className="btn-primary" data-testid="hero-cta-signup">
              Commencer gratuitement <ArrowRight className="h-4 w-4" />
            </Link>
            <button type="button" onClick={() => setShowVideo(true)} className="btn-secondary" data-testid="hero-cta-demo">
              <PlayCircle className="h-4 w-4 text-bleu" /> Découvrir INTEGRA FRANCE
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1 }}
            className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs font-medium text-slate-500"
          >
            <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-bleu" /> Simple</span>
            <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-emeraude" /> Accessible</span>
            <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-rouge" /> Accompagné</span>
          </motion.div>
        </div>

        <div className="relative">
          <motion.div style={{ y: yMockup }} initial={{ opacity: 0, y: 50, rotate: 1.5 }} animate={{ opacity: 1, y: 0, rotate: 0 }} transition={{ duration: 1.1, delay: 0.35, ease: EASE }}>
            <div className="card-integra overflow-hidden !rounded-3xl p-2" data-testid="hero-image-card">
              <img
                src="/hero-integra.jpg"
                alt="Quatre nouveaux arrivants souriants devant la tour Eiffel à Paris"
                className="aspect-[16/10] w-full rounded-2xl object-cover"
                loading="eager"
                data-testid="hero-image"
              />
            </div>
          </motion.div>

          <motion.div
            style={{ y: yBubble }}
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 1.2, ease: EASE }}
            className="absolute -top-32 right-0 w-64 sm:-right-4"
          >
            <div className="animate-floaty">
              <div className="card-integra p-4" data-testid="hero-chat-bubble">
                <p className="rounded-xl rounded-br-sm bg-bleu px-3.5 py-2.5 text-xs font-medium text-white">
                  Que dois-je faire maintenant ?
                </p>
                <div className="mt-2.5 flex items-start gap-2">
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-bleu-light">
                    <Bot className="h-4 w-4 text-bleu" />
                  </span>
                  <p className="rounded-xl rounded-tl-sm bg-slate-50 px-3.5 py-2.5 text-xs leading-relaxed text-slate-600">
                    Je vais vous guider étape par étape selon votre situation.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 1.45, ease: EASE }}
            className="absolute -bottom-6 left-0 hidden sm:block"
          >
            <div className="card-integra flex items-center gap-2.5 px-4 py-3">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-50">
                <FileCheck2 className="h-4 w-4 text-emeraude" />
              </span>
              <div>
                <p className="text-xs font-bold text-slate-900">Sources officielles</p>
                <p className="text-[10px] text-slate-400">Informations vérifiables</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {showVideo && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/85 p-4 backdrop-blur-sm"
          onClick={() => setShowVideo(false)} data-testid="video-modal">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.45, ease: EASE }}
            className="relative w-full max-w-3xl" onClick={(e) => e.stopPropagation()}
          >
            <button onClick={() => setShowVideo(false)} aria-label="Fermer la vidéo"
              className="absolute -top-10 right-0 flex items-center gap-1.5 text-sm font-semibold text-white/70 transition-colors hover:text-white"
              data-testid="video-modal-close">
              Fermer <X className="h-5 w-5" />
            </button>
            <video src="/decouvrir.mp4" controls playsInline preload="metadata"
              className="aspect-video w-full rounded-2xl bg-black shadow-2xl" data-testid="video-player" />
            <p className="mt-3 text-center text-xs text-white/50">INTEGRA FRANCE en moins d'une minute.</p>
          </motion.div>
        </div>
      )}
    </section>
  );
}
